import React, { useState } from 'react';
import {
  HelpCircle,
  SlidersHorizontal,
  Grid3x3,
  LineChart,
  PanelRight,
  MousePointerClick,
} from 'lucide-react';
import { ChartGuideModal } from '../ui';

interface EvaluationDashboardGuideProps {
  /** نام برند انتخاب‌شده برای شخصی‌سازی متن راهنما */
  brandName?: string;
  className?: string;
}

/**
 * راهنمای جامع داشبورد «نقشه حرارتی ارزیابی هوشمند».
 * یک دکمه نمایش می‌دهد که با کلیک روی آن، یک Modal کامل باز می‌شود و
 * نحوه‌ی استفاده، معنای هر داده و تفسیر سلول‌های نقشه حرارتی را توضیح می‌دهد.
 * این کامپوننت خودکفا است و state باز/بسته بودن Modal را خودش مدیریت می‌کند.
 */
const EvaluationDashboardGuide: React.FC<EvaluationDashboardGuideProps> = ({
  brandName,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const brandLabel = brandName ? `گروه ${brandName}` : 'گروه برند';

  return (
    <>
      {/* دکمه راهنما */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-accent-purple/40 bg-accent-purple/10 text-accent-purple text-sm font-semibold transition-all hover:bg-accent-purple/20 hover:border-accent-purple hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent-purple ${className}`}
        aria-label="راهنمای استفاده از داشبورد"
      >
        <HelpCircle size={18} />
        راهنمای داشبورد
      </button>

      <ChartGuideModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="راهنمای استفاده از نقشه حرارتی ارزیابی هوشمند"
      >
        <div className="space-y-10 text-right leading-relaxed">
          {/* مقدمه */}
          <section>
            <p className="text-text-secondary">
              این داشبورد عملکرد شعبه شما را در حوزه‌ی{' '}
              <span className="font-semibold text-text-primary">
                نظرسنجی و ارتباط با مشتری
              </span>{' '}
              در ماه‌های مختلف نمایش می‌دهد و آن را با میانگین سایر شعب مقایسه
              می‌کند. هدف، شناسایی سریعِ ماه‌های قوی و ضعیف و دلایل آن‌هاست.
            </p>
          </section>

          {/* بخش ۱: کنترل‌ها */}
          <GuideSection
            icon={<SlidersHorizontal size={20} />}
            title="۱) کنترل‌های بالای صفحه"
          >
            <p className="text-text-secondary mb-4">
              سه جعبه‌ی کنترلی در بالای داشبورد وجود دارد که خروجی نمودارها را
              تغییر می‌دهند:
            </p>
            <ul className="space-y-3">
              <GuideItem label="انتخاب شاخص">
                تعیین می‌کند کدام معیار روی نقشه حرارتی نمایش داده شود:{' '}
                <b>اشتراک‌گیری</b>، <b>درصد نظرات مثبت</b>،{' '}
                <b>مشارکت در نظرسنجی</b> یا <b>روزهای بدون شارژ پیامک</b>.
              </GuideItem>
              <GuideItem label="مقایسه با">
                مبنای مقایسه را مشخص می‌کند: <b>{brandLabel}</b> (میانگین شعب
                هم‌برند در همان ماه) یا <b>میانگین ۱۲ ماه</b> گذشته‌ی همین شعبه.
                رنگ هر سلول بر اساس همین مقایسه محاسبه می‌شود.
              </GuideItem>
              <GuideItem label="خطوط نمودار">
                خطوط کمکی <b>خط برند</b> و <b>میانگین ۱۲ ماه</b> را روی نمودار
                روند زیر نقشه روشن یا خاموش می‌کند تا مقایسه‌ی بصری ساده‌تر شود.
              </GuideItem>
            </ul>
          </GuideSection>

          {/* بخش ۲: نقشه حرارتی + سلول نمونه */}
          <GuideSection
            icon={<Grid3x3 size={20} />}
            title="۲) نقشه حرارتی چطور خوانده می‌شود؟"
          >
            <p className="text-text-secondary mb-4">
              هر <b>مربع</b> یک ماه را نشان می‌دهد. <b>رنگ</b> مربع نشان می‌دهد
              عملکرد آن ماه نسبت به مبنای مقایسه چقدر بهتر یا بدتر بوده است؛ از
              سبزِ پررنگ (عالی) تا قرمز (بحرانی). برای دیدن جزئیات کامل، کافی است
              روی هر ماه کلیک کنید.
            </p>

            {/* سلول نمونه‌ی آنوتیت‌شده */}
            <div className="bg-bg-secondary/50 rounded-xl p-5 border border-border-primary">
              <p className="text-sm font-semibold text-text-primary mb-4">
                یک سلول نمونه و معنای اعداد داخل آن:
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-6">
                {/* خود سلول نمونه (هم‌شکل با نقشه‌ی واقعی) */}
                <div className="shrink-0">
                  <div
                    className="heatmap-good relative aspect-square w-36 flex flex-col items-center justify-center p-4 rounded-xl shadow-lg"
                    style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                  >
                    <div className="text-center mb-1">
                      <p className="font-bold text-base text-text-inverse drop-shadow-md">
                        خرداد
                      </p>
                      <p className="font-bold text-base text-text-inverse drop-shadow-md">
                        ۱۴۰۴
                      </p>
                    </div>
                    <div className="text-center flex-1 flex flex-col justify-center">
                      <p className="text-lg font-bold text-text-inverse drop-shadow-md leading-tight">
                        ۴۵.۲٪
                      </p>
                      <div className="flex items-center justify-center mt-1 px-2 py-1 rounded-full text-xs font-medium intent-success-bg text-theme-inverse">
                        <span className="mr-1">↗</span>
                        <span>۸.۳٪</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* توضیح اجزای سلول */}
                <ul className="flex-1 space-y-3 w-full">
                  <AnnotationRow color="bg-accent-purple" title="نام و سال ماه">
                    این سلول مربوط به ماه «خرداد ۱۴۰۴» است.
                  </AnnotationRow>
                  <AnnotationRow color="bg-status-info" title="مقدار شاخص (۴۵.۲٪)">
                    مقدار واقعی شاخص انتخاب‌شده در این ماه. مثلاً اگر شاخص
                    «اشتراک‌گیری» باشد، یعنی ۴۵.۲٪ از فاکتورها منجر به اشتراک‌گیری
                    شده است.
                  </AnnotationRow>
                  <AnnotationRow color="bg-status-success" title="اختلاف (↗ ۸.۳٪)">
                    این ماه <b>۸.۳ واحد بهتر</b> از مبنای مقایسه بوده است. فلش رو
                    به بالا (↗) و رنگ سبز یعنی بالاتر از میانگین؛ فلش رو به پایین
                    (↘) و رنگ قرمز یعنی پایین‌تر از میانگین.
                  </AnnotationRow>
                  <AnnotationRow color="heatmap-good" title="رنگ پس‌زمینه">
                    شدت و جهت همین اختلاف را نشان می‌دهد. هرچه سبزتر، عملکرد بهتر؛
                    هرچه قرمزتر، نیازمند توجه بیشتر.
                  </AnnotationRow>
                </ul>
              </div>

              {/* راهنمای رنگ‌ها */}
              <div className="mt-5 pt-4 border-t border-border-primary">
                <p className="text-xs font-semibold text-text-secondary mb-3">
                  طیف رنگ‌ها بر اساس میزان اختلاف با مبنای مقایسه:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <LegendChip className="heatmap-excellent" label="عالی (+۱۵٪)" />
                  <LegendChip className="heatmap-good" label="خوب (+۱۰٪)" />
                  <LegendChip className="heatmap-suitable" label="مناسب (+۵٪)" />
                  <LegendChip className="heatmap-average" label="متوسط (±۲٪)" />
                  <LegendChip className="heatmap-poor" label="ضعیف (−۵٪)" />
                  <LegendChip className="heatmap-bad" label="نامناسب (−۱۰٪)" />
                  <LegendChip className="heatmap-critical" label="بحرانی (−۱۵٪)" />
                  <LegendChip className="heatmap-no-data" label="بدون داده" />
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-2 text-sm text-text-secondary bg-status-info-bg border border-status-info rounded-lg p-3">
              <MousePointerClick size={18} className="text-status-info shrink-0 mt-0.5" />
              <span>
                نکته: روی هر ماه کلیک کنید تا جزئیات کامل آن در پنل «تحلیل ماهانه»
                باز شود. کلیک دوباره، انتخاب را برمی‌دارد.
              </span>
            </div>
          </GuideSection>

          {/* بخش ۳: پنل تحلیل ماهانه */}
          <GuideSection
            icon={<PanelRight size={20} />}
            title="۳) پنل خلاصه و تحلیل ماهانه"
          >
            <p className="text-text-secondary mb-3">
              این پنل (کنار نقشه) در دو حالت کار می‌کند:
            </p>
            <ul className="space-y-3">
              <GuideItem label="حالت خلاصه">
                وقتی هیچ ماهی انتخاب نشده، میانگین کلی شعبه را نشان می‌دهد:
                میانگین اشتراک‌گیری، میانگین نظرات مثبت، کل سفارش‌ها و میانگین
                مشارکت.
              </GuideItem>
              <GuideItem label="حالت تحلیل ماهانه">
                با کلیک روی یک ماه، آمار دقیق همان ماه نمایش داده می‌شود: تعداد
                سفارش، درصد اشتراک‌گیری، تعداد و درصد نظرات راضی/ناراضی، مجموع
                نظرات، پیامک‌های ارسالی و روزهای بدون شارژ پیامک.
              </GuideItem>
            </ul>
          </GuideSection>

          {/* بخش ۴: نمودار روند */}
          <GuideSection
            icon={<LineChart size={20} />}
            title="۴) نمودار روند عملکرد"
          >
            <p className="text-text-secondary">
              زیر نقشه‌ی حرارتی، روند شاخص انتخاب‌شده در طول زمان به‌صورت خطی رسم
              می‌شود. با روشن‌کردن «خط برند» یا «میانگین ۱۲ ماه» از کنترل‌های بالا،
              می‌توانید روند شعبه را با این مبناها مقایسه کنید و ببینید فاصله‌ی
              شعبه با میانگین در حال کم یا زیاد شدن است.
            </p>
          </GuideSection>

          {/* جمع‌بندی شاخص‌ها */}
          <GuideSection
            icon={<HelpCircle size={20} />}
            title="معنی شاخص‌ها در یک نگاه"
          >
            <ul className="space-y-2">
              <GuideItem label="اشتراک‌گیری">
                درصد فاکتورهایی که اطلاعات تماس مشتری در آن‌ها ثبت شده است.
              </GuideItem>
              <GuideItem label="درصد نظرات مثبت">
                سهم نظرات راضی از کل نظرات ثبت‌شده.
              </GuideItem>
              <GuideItem label="مشارکت در نظرسنجی">
                درصد مشتریانی که به نظرسنجی پاسخ داده‌اند.
              </GuideItem>
              <GuideItem label="روزهای بدون شارژ پیامک">
                تعداد روزهایی که به‌دلیل اتمام شارژ، امکان ارسال پیامک نبوده است
                (هرچه کمتر، بهتر).
              </GuideItem>
            </ul>
          </GuideSection>
        </div>
      </ChartGuideModal>
    </>
  );
};

/* ---------- اجزای کمکی داخلی ---------- */

const GuideSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <section>
    <div className="flex items-center gap-2 mb-3">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-accent-purple/10 text-accent-purple shrink-0">
        {icon}
      </span>
      <h3 className="text-lg sm:text-xl font-semibold text-text-primary">
        {title}
      </h3>
    </div>
    {children}
  </section>
);

const GuideItem: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <li className="bg-bg-secondary/40 rounded-lg p-3 border border-border-primary">
    <span className="font-semibold text-text-primary">{label}: </span>
    <span className="text-text-secondary">{children}</span>
  </li>
);

const AnnotationRow: React.FC<{
  color: string;
  title: string;
  children: React.ReactNode;
}> = ({ color, title, children }) => (
  <li className="flex items-start gap-3">
    <span
      className={`${color} w-4 h-4 rounded-md shrink-0 mt-1 border border-border-primary`}
    />
    <div>
      <p className="font-semibold text-text-primary text-sm">{title}</p>
      <p className="text-text-secondary text-sm">{children}</p>
    </div>
  </li>
);

const LegendChip: React.FC<{ className: string; label: string }> = ({
  className,
  label,
}) => (
  <div className="flex items-center gap-2">
    <span
      className={`${className} w-4 h-4 rounded border border-border-primary shrink-0`}
    />
    <span className="text-xs text-text-secondary">{label}</span>
  </div>
);

export default EvaluationDashboardGuide;
